import { PayrollBroker } from './payroll-broker-dialog/payroll-broker-dialog.component';

export type PayrollBrokerConflictDecision = 'leave' | 'move';

export interface PayrollBrokerCategoryConflict {
  category: string;
  currentBrokerId: string;
  currentBrokerName: string;
  newBrokerId: string;
  newBrokerName: string;
}

export function getPayrollBrokerCategoryConflict(
  existingBrokers: PayrollBroker[],
  brokerId: string,
  brokerName: string,
  category: string,
): PayrollBrokerCategoryConflict | undefined {
  const currentBroker = existingBrokers
    .filter(broker => broker.id !== brokerId)
    .find(broker => (broker.categories || []).includes(category));

  if (!currentBroker) {
    return undefined;
  }

  return {
    category,
    currentBrokerId: currentBroker.id,
    currentBrokerName: currentBroker.companyName,
    newBrokerId: brokerId,
    newBrokerName: brokerName,
  };
}

export function buildPayrollBrokerCategoryConflicts(
  existingBrokers: PayrollBroker[],
  submittedBroker: PayrollBroker,
): PayrollBrokerCategoryConflict[] {
  const otherBrokers = existingBrokers.filter(broker => broker.id !== submittedBroker.id);
  const submittedCategories = getUniqueBrokerCategories(submittedBroker.categories);

  return submittedCategories.reduce((conflicts, category) => {
    const currentBroker = otherBrokers.find(broker => (broker.categories || []).includes(category));
    if (!currentBroker) {
      return conflicts;
    }

    conflicts.push({
      category,
      currentBrokerId: currentBroker.id,
      currentBrokerName: currentBroker.companyName,
      newBrokerId: submittedBroker.id,
      newBrokerName: submittedBroker.companyName,
    });

    return conflicts;
  }, [] as PayrollBrokerCategoryConflict[]);
}

export function normalizePayrollBrokerAssignments(
  existingBrokers: PayrollBroker[],
  submittedBroker: PayrollBroker,
  decisions: Record<string, PayrollBrokerConflictDecision> = {},
): PayrollBroker[] {
  const submittedCategories = getUniqueBrokerCategories(submittedBroker.categories);
  const submittedCategorySet = new Set(submittedCategories);
  const moveCategorySet = new Set(
    Object.entries(decisions)
      .filter(([, decision]) => decision === 'move')
      .map(([category]) => category),
  );
  const resolvedSubmittedCategories = submittedCategories.filter(category => decisions[category] !== 'leave');
  const normalizedSubmittedBroker = {
    ...submittedBroker,
    categories: resolvedSubmittedCategories,
  };

  let foundSubmittedBroker = false;
  const normalizedBrokers = existingBrokers.map(broker => {
    if (broker.id === submittedBroker.id) {
      foundSubmittedBroker = true;
      return normalizedSubmittedBroker;
    }

    const categories = getUniqueBrokerCategories(broker.categories).filter(category => {
      return !(submittedCategorySet.has(category) && moveCategorySet.has(category));
    });

    return {
      ...broker,
      categories,
    };
  });

  if (!foundSubmittedBroker) {
    normalizedBrokers.push(normalizedSubmittedBroker);
  }

  return normalizedBrokers;
}

export function getUniqueBrokerCategories(categories: string[] | null | undefined): string[] {
  const seen = new Set<string>();

  return Array.isArray(categories)
    ? categories.filter((category): category is string => typeof category === 'string' && !!category).filter(category => {
        if (seen.has(category)) {
          return false;
        }

        seen.add(category);
        return true;
      })
    : [];
}
